import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { collection, getDocs, getDoc, doc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../AuthContext';

/**
 * Composant pour gérer les administrateurs et leurs postes
 */
const Administrators = () => {
  const [administrators, setAdministrators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: ''
  });
  const [isPDG, setIsPDG] = useState(false);
  const { currentUser } = useAuth();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Vérifier si l'utilisateur actuel est PDG
  useEffect(() => {
    const checkUserRole = async () => {
      if (currentUser) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists() && userDoc.data().role === 'PDG') {
            setIsPDG(true);
          }
        } catch (err) {
          console.error('Erreur lors de la vérification du rôle:', err);
        }
      }
    };
    
    checkUserRole();
  }, [currentUser]);

  // Charger la liste des administrateurs
  useEffect(() => {
    const fetchAdministrators = async () => {
      try {
        setLoading(true);
        
        const adminQuery = query(
          collection(db, 'users'),
          where('role', 'in', ['admin', 'PDG'])
        );
        
        const adminSnapshot = await getDocs(adminQuery);
        const adminsList = adminSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setAdministrators(adminsList);
      } catch (err) {
        console.error('Erreur lors du chargement des administrateurs:', err);
        setError('Impossible de charger la liste des administrateurs. ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAdministrators();
  }, []);

  const handleOpenDialog = (admin = null) => {
    if (admin) {
      // Vérifier si l'utilisateur a le droit de modifier cet administrateur
      if (!isPDG && admin.id !== currentUser?.uid) {
        setError('Vous n\'avez pas les permissions nécessaires pour modifier les informations d\'autres administrateurs.');
        setTimeout(() => setError(''), 5000);
        return;
      }
      setSelectedAdmin(admin);
      setFormData({
        name: admin.name || '',
        email: admin.email || '',
        position: admin.position || ''
      });
    } else {
      if (!isPDG) {
        setError('Seul le PDG peut ajouter de nouveaux administrateurs.');
        setTimeout(() => setError(''), 5000);
        return;
      }
      setSelectedAdmin(null);
      setFormData({ name: '', email: '', position: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAdmin(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (selectedAdmin) {
        // Vérifier à nouveau les permissions
        if (!isPDG && selectedAdmin.id !== currentUser?.uid) {
          setError('Vous n\'avez pas les permissions nécessaires pour modifier les informations d\'autres administrateurs.');
          return;
        }

        const adminRef = doc(db, 'users', selectedAdmin.id);
        const updateData = {};

        // Seul le PDG peut modifier le poste
        if (isPDG) {
          updateData.position = formData.position;
        }

        // Tout le monde peut modifier son propre nom
        if (isPDG || selectedAdmin.id === currentUser?.uid) {
          updateData.name = formData.name;
        }
        
        await updateDoc(adminRef, updateData);
        setAdministrators(prevAdmins => 
          prevAdmins.map(admin => 
            admin.id === selectedAdmin.id 
              ? { ...admin, ...updateData } 
              : admin
          )
        );
        
        setSuccessMessage('Administrateur mis à jour avec succès.');
      }
      
      handleCloseDialog();
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
      setError('Une erreur est survenue lors de la sauvegarde. ' + err.message);
    } finally {
      setLoading(false);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  };

  const handleDelete = async (adminId) => {
    if (!isPDG) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet administrateur ?')) {
      return;
    }
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'users', adminId));
      setAdministrators(prevAdmins => prevAdmins.filter(admin => admin.id !== adminId));
      setSuccessMessage('Administrateur supprimé avec succès.');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Une erreur est survenue lors de la suppression. ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box >
      {/* En-tête avec les informations de l'utilisateur connecté */}
      

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>
      )}

      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography variant="h5" sx={{ color: '#014527' }}>
          Gestion des Administrateurs
        </Typography>
        
        {isPDG && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              bgcolor: '#014527',
              '&:hover': { bgcolor: '#013320' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Ajouter un administrateur
          </Button>
        )}
      </Box>

      <Grid container spacing={2} direction={isMobile ? 'column' : 'row'}>
        {administrators.map((admin) => (
          <Grid item xs={12} sm={6} md={4} key={admin.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">{admin.name}</Typography>
                  <Chip label={admin.position} color="primary" />
                </Box>
                <Typography variant="body2" color="textSecondary">{admin.email}</Typography>
              </CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                <IconButton onClick={() => handleOpenDialog(admin)}><EditIcon /></IconButton>
                <IconButton onClick={() => handleDelete(admin.id)}><DeleteIcon /></IconButton>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedAdmin ? 'Modifier l\'administrateur' : 'Ajouter un administrateur'}
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            margin="normal"
            required
          />
          
          <TextField
            fullWidth
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
            required
            disabled={selectedAdmin}
            type="email"
          />
          
          {isPDG && (
            <TextField
              fullWidth
              label="Poste"
              name="position"
              value={formData.position}
              onChange={handleChange}
              margin="normal"
            />
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            sx={{ bgcolor: '#014527', '&:hover': { bgcolor: '#013320' } }}
          >
            {selectedAdmin ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Administrators;